import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../../components/common/Logo';
import { authService } from '../../services';
import { validatePassword } from '../../utils/validation';

const ResetPassword = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const tokenFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('token') || '').trim();
  }, [location.search]);

  const [token, setToken] = useState(tokenFromQuery);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const invalidTokenMessage =
    t('auth.resetPassword.invalidOrExpired') ||
    'Reset link expired or invalid. Please request a new one.';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token.trim()) {
      setError(t('auth.resetPassword.tokenRequired'));
      return;
    }

    const pwdErr = validatePassword(newPassword);
    if (pwdErr) {
      setError(pwdErr);
      return;
    }
    if (confirmPassword && confirmPassword !== newPassword) {
      setError(t('auth.resetPassword.passwordMismatch'));
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword({ token: token.trim(), newPassword });
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || '';
      const msgLower = String(msg).toLowerCase();

      if (status === 400 && (msgLower.includes('expired') || msgLower.includes('invalid jwt token'))) {
        setError(invalidTokenMessage);
      } else {
        setError(msg || (t('common.error') || 'Error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    navigate('/forgot-password', { replace: false });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="lg" showText={false} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">{t('auth.resetPassword.title')}</h2>
          <p className="mt-2 text-gray-600">{t('auth.resetPassword.subtitle')}</p>
        </div>

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded-lg">
            {t('auth.resetPassword.successMessage')}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
            {(error === invalidTokenMessage) && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-primary-700 font-semibold hover:text-primary-800 underline"
                >
                  {t('auth.resetPassword.resendLink')}
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="token" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.resetPassword.token')}
            </label>
            <input
              id="token"
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
              placeholder={t('auth.resetPassword.tokenPlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.resetPassword.newPassword')} <span className="text-red-500">*</span>
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
              placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
              required
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.resetPassword.confirmPassword')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
              placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
            />
          </div>

          <button
            type="submit"
            disabled={loading || success}
            className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('common.loading') : t('auth.resetPassword.submit')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-primary-600 font-semibold hover:text-primary-700">
            ← {t('auth.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

